import { useEffect, useState, Fragment } from "react";
import HeaderNav from "components/FutureNavBasedComponents/HeaderNav";
import UnderHeaderSection from "components/FutureNavBasedComponents/PageSubheader/UnderHeaderSection";
import PillButton from "components/FutureNavBasedComponents/PillButton";
import TwoPanelView from "components/FutureNavBasedComponents/TwoPanelView";
import ChevronCollapsible from "components/FutureNavBasedComponents/ChevronCollapsible";
import CheckBox from "components/FutureNavBasedComponents/CheckBox";
import { useSelector, useDispatch } from "react-redux";
import PdfViewerMUI from "components/FutureNavBasedComponents/PdfViewer/PdfViewer";
import useAIHelperNavigation from "../../../hooks/useAIHelperNavigation";
import AIBotIcon from "../../../static/icons/ai-bot.svg";
import { indexBasedColorStyles } from "./data/common-data";
import useFormattedUserName from "./hooks/useFormattedUserName";
import useCurrentLearnerId from "./hooks/useCurrentLearnerId";
import { ArtifactDetailsArea, ArtifactDetailsCredit } from "redux/slices/artifactDetails/artifactDetailsSlice";
import { IDKey, UpdateBulkSubskillAssociationsRequestBody } from "services/educatorService.types";
import educatorService from "services/educatorService";
import { AIConfidenceLevel, AISelectedArea, aiScoreConfidenceValues } from "redux/slices/artifactAISkillSelections/artifactAISkillSelections";
import ConfidenceIndicator from "components/FutureNavBasedComponents/ConfidenceIndicator";
import useSkillUtilities from "./hooks/useSkillUtilities";
import { AssociatedSkillsArea, AssociatedSkillsCredit } from "redux/slices/associatedSkills/associatedSkillsSlice";
import { setAIHelperProgressRedux, SkillSelectionDisplayInfo, SelectedAreasById, SkillReviewProgressCredit } from "redux/slices/aiHelperToolProgress/aiHelperProgressSlice";

const ViewSubskills = ({ credits }: { credits: any[] }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mt-3">
      <button
        className="flex items-center gap-2 text-xs font-normal cursor-pointer text-[rgba(0,_125,_119,_1)]"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>View Subskills</span>
        <i className={`fa-solid text-[10px] text-[rgba(0,_125,_119,_1)] ${isOpen ? "fa-chevron-up" : "fa-chevron-down"}`} />
      </button>
      {isOpen && (
        <ul className="list-disc text-xs ml-6 mt-2">
          {credits.map((credit: any) => (
            <li key={credit.id}>{credit.title}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

interface MixedCredit extends AssociatedSkillsCredit {
  evidence_progress_id?: number | null;
}
interface MixedArea extends AssociatedSkillsArea {
  credits: MixedCredit[];
}

const AIHelperSkillsReview = () => {
  const { handleBackToList, handleGoToSubskillsReview, handleGoToGetStarted } = useAIHelperNavigation();
  const { firstName, userName } = useFormattedUserName();
  const { sortAreasAISelectionFirst, getAreaSubskillScoresSum } = useSkillUtilities();

  // object used primarily for display of selected skills

  // object used primarily for generating request body when user clicks next
  // const [selectedAreas, setSelectedAreas] = useState<{ [key: string]: MixedArea }>({});
  const [initialAssociatedAreas, setInitialAssociatedAreas] = useState<{ [key: string]: MixedArea }>({});

  const associatedSkillsRedux = useSelector((state: any) => state.associatedSkills?.data) || {};
  const artifactDetails = useSelector((state: any) => state.artifactDetails?.data);
  const aiHelperProgressRedux = useSelector((state: any) => state.aiHelperProgress?.data);
  const artifactAISkillSelectionsRedux = useSelector((state: any) => state.artifactAISkillSelections?.data);
  const dispatch = useDispatch();

  const selectedAreasDisplayInfoRedux = aiHelperProgressRedux?.skillsReview?.selectedAreasDisplayInfo || {};
  const selectedAreasByIdRedux: SelectedAreasById = aiHelperProgressRedux?.skillsReview?.selectedAreasById || {};

  // When isPlanningLibrary is true, the API returns the parent as the main model and the
  // school-specific child as schoolModel. For skill review we always want the school model.
  const effectiveAssociatedSkills = associatedSkillsRedux?.schoolModel || associatedSkillsRedux;

  // All available skills from effectiveAssociatedSkills
  const associatedSkillsReduxAreas: AssociatedSkillsArea[] = effectiveAssociatedSkills?.areas || [];
  const sortedAssociatedSkillsReduxAreas: AssociatedSkillsArea[] = sortAreasAISelectionFirst(associatedSkillsReduxAreas);

  // Artifact-matched skill IDs
  const artifactSkillIds: Set<number> = new Set(
    (artifactDetails?.areas || []).map((area: any) => area.id)
  );
  const getCurrentLearnerId = useCurrentLearnerId();

  // Populate selected skills and areas based on matching IDs
  useEffect(() => {
    if (associatedSkillsReduxAreas.length === 0) return;
    let initialSkillSelectionStatuses: SkillSelectionDisplayInfo = {};
    let initialSelectedAreas: { [key: string]: MixedArea } = {};

    associatedSkillsReduxAreas.forEach((skill) => {
      const aiAreaSelection = artifactAISkillSelectionsRedux ? artifactAISkillSelectionsRedux.find((aiAreaSelection: AISelectedArea) => aiAreaSelection.skill_name === skill.title) : null;
      const isAISelected = !!aiAreaSelection && getAreaSubskillScoresSum(aiAreaSelection) > 0; // if ai selection has no subskill scores, we consider it not selected by AI.
      const isMatched = artifactSkillIds.has(skill.id) || isAISelected;
      initialSkillSelectionStatuses[skill.id] = {
        isSelected: isMatched, 
        wasSelectedByAI: isAISelected, 
      };

      if (isMatched) {
        // Use skill from skillsRedux (has ALL credits/subskills) but merge evidence_progress_id
        // from artifactDetails credits so dissociation works
        const artifactArea = (artifactDetails?.areas || []).find((a: ArtifactDetailsArea) => a.id === skill.id);
        if (artifactArea?.credits) {
          const mergedCredits = (skill.credits || []).map((credit: AssociatedSkillsCredit) => {
            const matchingCredit = artifactArea.credits.find((c: ArtifactDetailsCredit) => c.id === credit.id);
            return matchingCredit?.evidence_progress_id
              ? { ...credit, evidence_progress_id: matchingCredit.evidence_progress_id }
              : credit;
          });
          initialSelectedAreas[skill.id] = { ...skill, credits: mergedCredits };
        } else {
          initialSelectedAreas[skill.id] = skill;
        }
      }
    });

    dispatch(setAIHelperProgressRedux({
      ...aiHelperProgressRedux,

      // defaults to saved state.
      skillsReview: {
        selectedAreasById: aiHelperProgressRedux.skillsReview?.selectedAreasById || initialSelectedAreas,
        selectedAreasDisplayInfo: aiHelperProgressRedux.skillsReview?.selectedAreasDisplayInfo || initialSkillSelectionStatuses,
      }
    }));
    
    setInitialAssociatedAreas(initialSelectedAreas);
  }, []);

  const getSubskillsDissociationRequestBody = () => {
    const initialAssociatedAreaIds = Object.keys(initialAssociatedAreas);
    const currentlySelectedAreaIds = Object.keys(selectedAreasByIdRedux);

    const requestBody: { evidence_progress_ids: IDKey[] } = {
      evidence_progress_ids: [],
    };

    // we look for associated subskills within the skills that have been removed to dissociate
    // Use initialAssociatedAreas (which has merged evidence_progress_id) instead of artifactDetails.areas
    initialAssociatedAreaIds.forEach((initiallyAssociatedAreaId: string) => {
      // a skill that was initially associated has been deselected, so we dissociate the related subskills.
      if (!currentlySelectedAreaIds.includes(initiallyAssociatedAreaId)) {
        const currentAssociatedArea = initialAssociatedAreas[initiallyAssociatedAreaId];
        currentAssociatedArea?.credits?.forEach((credit: MixedCredit) => {
          if (credit.evidence_progress_id) {
            requestBody.evidence_progress_ids.push(credit.evidence_progress_id);
          }
        });
      }
    });

    return requestBody;
  };

  const updateStoreWithSelectedAreas = () => {
    // Merge evidence_progress_id from artifactDetails credits into the full skillsRedux credits
    // so all subskills remain visible while preserving evidence_progress_id for dissociation
    const updatedAreas = Object.values(selectedAreasByIdRedux).map((area) => {
      const existingArea = (artifactDetails?.areas || []).find((a: ArtifactDetailsArea) => a.id === area.id);
      if (existingArea?.credits && area.credits) {
        const mergedCredits = area.credits.map((credit: SkillReviewProgressCredit) => {
          const matchingCredit = existingArea.credits.find((c: ArtifactDetailsCredit) => c.id === credit.id);
          return matchingCredit?.evidence_progress_id
            ? { ...credit, evidence_progress_id: matchingCredit.evidence_progress_id }
            : credit;
        });
        return { ...area, credits: mergedCredits };
      }
      return area;
    });
    dispatch(setAIHelperProgressRedux({ ...aiHelperProgressRedux, skillReview: { finalSkillSelections: updatedAreas } }));
  };

  const handleSkillToggle = (area: MixedArea, newCheckedStatus: boolean) => {
    const previousAreaDisplayInfo = aiHelperProgressRedux.skillsReview?.selectedAreasDisplayInfo?.[area.id];

    if (newCheckedStatus === true) {
      dispatch(setAIHelperProgressRedux({
        ...aiHelperProgressRedux,
        skillsReview: {
          ...aiHelperProgressRedux.skillsReview,

          // adding area to selectedAreasById
          selectedAreasById: {
            ...selectedAreasByIdRedux,
            [area.id]: area,
          },

          // updating display info to reflect new selection
          selectedAreasDisplayInfo: {
            ...selectedAreasDisplayInfoRedux,
            [area.id]: {
              ...previousAreaDisplayInfo,
              isSelected: true,
            }
          }
        }
      }));
    } else {
      const newSelectedAreasById = { ...selectedAreasByIdRedux };
      delete newSelectedAreasById[area.id];

      dispatch(setAIHelperProgressRedux({
        ...aiHelperProgressRedux,
        skillsReview: {
          ...aiHelperProgressRedux.skillsReview,
          selectedAreasById: newSelectedAreasById,
          selectedAreasDisplayInfo: {
            ...selectedAreasDisplayInfoRedux,
            [area.id]: {
              ...previousAreaDisplayInfo,
              isSelected: false,
            }
          }
        }
      }));
    }
  }

  return (
    <div className="tailwind-page font-beausite text-typography-charcoal pb-5">
      <div className="max-w-screen mx-[13px]">
        <HeaderNav />
      </div>

      <div className="flex justify-between">
        <UnderHeaderSection
          backButtonLabel="Learner Artifacts List"
          onBackButtonClick={() => { handleBackToList() }}
          pageMainHeader="Add or Remove Primary Skills"
        />

        {/* <div className="flex items-center pr-[31px]">
          <PillButton onClick={() => {}} label="Get Started" variant="dark" />
        </div> */}
      </div>
      <TwoPanelView
        leftPanelSubheader={(
          <div className="flex items-start mb-4">
            <img src={AIBotIcon} alt="AI selected skill indicator" className="mr-1 p-[2px]" />
            <p className="text-sm">We observed the selected skills in the learner's Artifact. Does this reflect your observations? Select or deselect skills to match your observations.</p>
          </div>
        )}
        LeftPanelContent={
          (<>
            {sortedAssociatedSkillsReduxAreas.map((skill, skillIndex) => {
              const wasSkillSelectedByAI = selectedAreasDisplayInfoRedux[skill.id]?.wasSelectedByAI;
              const aiAreaSelection = artifactAISkillSelectionsRedux ? artifactAISkillSelectionsRedux.find((aiAreaSelection: AISelectedArea) => aiAreaSelection.skill_name === skill.title) : null;
              const isCurrentlySelected = selectedAreasDisplayInfoRedux[skill.id]?.isSelected || false;

              return (
                <ChevronCollapsible
                  key={skill.id}
                  HeaderContent={(
                    <div className="flex h-full items-center">
                      <CheckBox
                        className="mr-[15px]"
                        checked={selectedAreasDisplayInfoRedux[skill.id]?.isSelected || false}
                        onChange={() => {
                          // setSelectedAreasById(skill, !isCurrentlySelected);
                          // toggleIsSelectedStatus(skill.id, !isCurrentlySelected);
                          handleSkillToggle(skill, !isCurrentlySelected);
                        }}
                      />
                      <p className="font-semibold">{skill.title}</p>
                      {
                        wasSkillSelectedByAI && (
                          <img src={AIBotIcon} alt="AI selected skill indicator" className="ml-1" />
                        )
                      }
                    </div>
                  )}
                  BodyContent={(
                    <div className="w-full pb-7">
                      <h5 className="text-sm mb-1">Details</h5>
                      <ul className="list-disc text-xs ml-6">
                        { (wasSkillSelectedByAI && aiAreaSelection) && (
                          <>
                            <li className="mb-4">
                              <p><b>Reason: </b> {aiAreaSelection.skill_reason}</p>
                            </li>
                            <li className="mb-4">
                              <p><b>Confidence: </b> The AI observed <b>{skill.title}</b> with the following level of confidence:</p>
                              <ConfidenceIndicator
                                confidenceLevel={aiScoreConfidenceValues[aiAreaSelection.skill_confidence as AIConfidenceLevel]}
                                className="mt-1"
                              />
                            </li>
                          </>
                        )}
                        <li>
                          <p><b>Definition: </b> {skill.title}</p>
                          <p className="ml-2 mt-1">{skill.description}</p>
                        </li>
                      </ul>
                      {skill.credits && skill.credits.length > 0 && (
                        <ViewSubskills credits={skill.credits} />
                      )}
                    </div>
                  )}
                  className={`${indexBasedColorStyles[skillIndex % indexBasedColorStyles.length]} mb-[10px]`}
                />
              )
            })}

            <legend className="flex items-center mt-[6px]">
              <img src={AIBotIcon} alt="AI selected skill indicator" className="mx-1" />
              <p>An AI Identified Skill</p>
            </legend>

            <div className="flex items-center mt-6">
              <PillButton variant="light" label="Back" onClick={() => { handleGoToGetStarted() }} outerDivClassName="mr-[14px]" />
              <PillButton variant="dark" label="Next" onClick={async () => {
                const learnerId = getCurrentLearnerId();
                await educatorService.updateBulkSubskillAssociations({
                  artifactId: artifactDetails.id,
                  learnerId,
                  requestBody: getSubskillsDissociationRequestBody(),
                });
                updateStoreWithSelectedAreas();
                handleGoToSubskillsReview();
              }} />
            </div>
          </>)
        }
        rightPanelHeader={artifactDetails?.title}
        rightPanelSubheader={`Learner: ${userName}`}
        RightPanelContent={(<PdfViewerMUI file={artifactDetails?.fileUrl} />)}
      />
    </div>
  );
};

export default AIHelperSkillsReview;

